import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { acceptCurrentTerms, getCurrentTerms } from "./api";
import { useWalletAuth } from "./walletAuth";

const TermsAcceptanceContext = createContext(null);

export function TermsAcceptanceProvider({ children }) {
  const { disconnectAndLogout, isAuthenticated, loadingSession, refreshSession, session } = useWalletAuth();
  const [currentTerms, setCurrentTerms] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const acceptedCurrentTerms = Boolean(session?.terms?.acceptedCurrentTerms);
  const termsUnavailable = session?.terms?.status === "unavailable";

  const refreshTermsStatus = useCallback(async () => refreshSession(), [refreshSession]);

  const loadCurrentTerms = useCallback(async () => {
    const terms = await getCurrentTerms();
    setCurrentTerms(terms);
    return terms;
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setModalOpen(false);
      setBlocking(false);
      setError("");
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (loadingSession || !isAuthenticated || acceptedCurrentTerms) return;
    loadCurrentTerms().catch(() => setError("Current terms are temporarily unavailable."));
    setBlocking(false);
    setModalOpen(true);
  }, [acceptedCurrentTerms, isAuthenticated, loadCurrentTerms, loadingSession]);

  const requireCurrentTerms = useCallback(
    async ({ surface = "modal", returnTo = "/" } = {}) => {
      void surface;
      void returnTo;
      if (!isAuthenticated) return false;
      if (termsUnavailable) {
        setError("Current terms are temporarily unavailable.");
        setBlocking(true);
        setModalOpen(true);
        return false;
      }
      if (acceptedCurrentTerms) return true;
      await loadCurrentTerms().catch(() => null);
      setBlocking(true);
      setModalOpen(true);
      return false;
    },
    [acceptedCurrentTerms, isAuthenticated, loadCurrentTerms, termsUnavailable]
  );

  const acceptTerms = useCallback(
    async ({ acceptanceSurface = "modal", acceptedFromPath = "/" } = {}) => {
      const terms = currentTerms || (await loadCurrentTerms());
      setBusy(true);
      setError("");
      try {
        await acceptCurrentTerms({
          termsVersionId: terms.id,
          contentSha256: terms.contentSha256,
          acceptanceSurface,
          acceptedFromPath
        });
        await refreshSession();
        setModalOpen(false);
        setBlocking(false);
        return true;
      } catch (caught) {
        setError(caught?.message || "Could not record terms acceptance.");
        return false;
      } finally {
        setBusy(false);
      }
    },
    [currentTerms, loadCurrentTerms, refreshSession]
  );

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setBlocking(false);
  }, []);

  const value = useMemo(
    () => ({
      acceptedCurrentTerms,
      termsUnavailable,
      currentTerms,
      modalOpen,
      blocking,
      busy,
      error,
      acceptTerms,
      closeModal,
      disconnectAndLogout,
      refreshTermsStatus,
      requireCurrentTerms
    }),
    [
      acceptedCurrentTerms,
      acceptTerms,
      blocking,
      busy,
      closeModal,
      currentTerms,
      disconnectAndLogout,
      error,
      modalOpen,
      refreshTermsStatus,
      requireCurrentTerms,
      termsUnavailable
    ]
  );

  return <TermsAcceptanceContext.Provider value={value}>{children}</TermsAcceptanceContext.Provider>;
}

export function useTermsAcceptance() {
  const context = useContext(TermsAcceptanceContext);
  if (!context) {
    throw new Error("useTermsAcceptance must be used inside TermsAcceptanceProvider.");
  }
  return context;
}
